import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { hasCode, type ChatTurn } from "@rekati/api";
import { Button } from "@rekati/ui";
import { cn } from "@rekati/utils";
import IconClose from "~icons/solar/close-circle-linear";
import IconSend from "~icons/solar/plain-2-linear";
import IconRestart from "~icons/solar/restart-linear";
import { staffApi } from "../../app/api";
import IFORTE_AI_MARK from "../../assets/brand/iforte-ai-mark.png";
import { keys } from "../../app/keys";
import { useI18n } from "../../content/i18n";
import { useStaffError } from "../shell/staff-error";
import { KaraText } from "./KaraText";

const MAX_TURNS = 20;
const MAX_CHARS = 4000;

interface Turn extends ChatTurn {
  tools?: string[];
}

/** online: answering; offline: set up but the model is unreachable; unset: no assistant on this server. */
type KaraState = "online" | "offline" | "unset";

/** The conversation, kept above the panel so closing it (a click outside) never loses it. */
function useKaraChat(onRecheck: () => void) {
  const { t } = useI18n();
  const message = useStaffError();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: (history: Turn[]) =>
      staffApi.aiChat(history.slice(-MAX_TURNS).map(({ role, content }) => ({ role, content }))),
    onSuccess: (res) => setTurns((h) => [...h, { ...res.message, tools: res.tools_used }]),
    onError: (e, history) => {
      // Keep the question in the box so it can be sent again.
      setTurns(history.slice(0, -1));
      setDraft(history.at(-1)?.content ?? "");
      if (hasCode(e, "AI_UNAVAILABLE")) setError(t("kara.unavailable"));
      else if (hasCode(e, "AI_NOT_CONFIGURED")) onRecheck();
      else setError(message(e));
    },
  });

  const send = (text: string) => {
    const content = text.trim().slice(0, MAX_CHARS);
    if (!content || ask.isPending) return;
    const history: Turn[] = [...turns, { role: "user", content }];
    setTurns(history);
    setDraft("");
    setError(null);
    ask.mutate(history);
  };

  return { turns, draft, setDraft, error, pending: ask.isPending, send, restart: () => setTurns([]) };
}

type KaraChat = ReturnType<typeof useKaraChat>;

/**
 * Kara, the organisers' assistant (docs/frontend-integration.md 6.6). The
 * server keeps nothing: the conversation lives here and goes whole with each
 * question, the last twenty turns. Shown as offline when it cannot reach the
 * model, and as not set up when the server has no assistant configured (a
 * local stack), so the panel is always there for the organisers. The panel
 * closes on a click outside it or Escape, and focus returns to the launcher.
 */
export function Kara() {
  const { t } = useI18n();
  const status = useQuery({
    queryKey: keys.aiStatus,
    queryFn: staffApi.aiStatus,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const chat = useKaraChat(() => void status.refetch());
  const launcher = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (wasOpen.current && !open) launcher.current?.focus();
    wasOpen.current = open;
  }, [open]);

  if (status.isPending) return null;
  const state: KaraState = !status.data?.configured
    ? "unset"
    : status.data.reachable && status.data.model_present
      ? "online"
      : "offline";

  return (
    <>
      {/* The iForte.AI mark in a circle under a turning volt ring; it steps aside while the chat is open. */}
      <button
        ref={launcher}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t("kara.launch")}
        title={t("kara.launch")}
        className={cn(
          "group fixed right-6 bottom-6 z-30 grid size-14 place-items-center rounded-full outline-none sm:right-8 sm:bottom-8",
          open && "hidden",
        )}
      >
        <span
          className="absolute -inset-[7px] rounded-full bg-[conic-gradient(from_0deg,var(--color-volt),transparent_35%,transparent_55%,var(--color-volt))] motion-safe:animate-[spin_3s_linear_infinite] motion-reduce:bg-volt group-hover:motion-safe:animate-[spin_1.2s_linear_infinite]"
          aria-hidden="true"
        />
        <span className="absolute -inset-1 rounded-full bg-background" aria-hidden="true" />
        <span className="relative grid size-14 place-items-center rounded-full bg-card shadow-lg ring-1 ring-border transition-transform duration-200 group-hover:scale-105 group-focus-visible:ring-4 group-focus-visible:ring-ring/40 group-active:scale-95">
          <img src={IFORTE_AI_MARK} alt="" className="size-8" draggable={false} />
        </span>
      </button>
      <AnimatePresence>
        {open ? <KaraPanel state={state} chat={chat} onClose={() => setOpen(false)} /> : null}
      </AnimatePresence>
    </>
  );
}

function KaraPanel({ state, chat, onClose }: { state: KaraState; chat: KaraChat; onClose: () => void }) {
  const { t } = useI18n();
  const online = state === "online";
  const { turns, draft, setDraft, error, pending, send } = chat;
  const panel = useRef<HTMLElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  // Focus lands in the panel: the question box, or the close button when Kara cannot answer.
  useEffect(() => {
    if (input.current && !input.current.disabled) input.current.focus();
    else panel.current?.querySelector<HTMLButtonElement>("[data-kara-close]")?.focus();
  }, []);
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight, behavior: "smooth" });
  }, [turns]);

  // Light dismiss: a press anywhere outside the panel, or Escape from anywhere, closes it.
  useEffect(() => {
    const outside = (e: PointerEvent) => {
      if (panel.current && e.target instanceof Node && !panel.current.contains(e.target)) onClose();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
      document.removeEventListener("keydown", escape);
    };
  }, [onClose]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send(draft);
  };

  const suggestions = [t("kara.try1"), t("kara.try2"), t("kara.try3")];

  return (
    <motion.aside
      ref={panel}
      role="dialog"
      aria-modal="false"
      aria-labelledby="kara-title"
      className="fixed inset-x-0 bottom-0 z-40 flex h-[min(85dvh,640px)] flex-col overflow-hidden rounded-t-2xl border border-border bg-card shadow-2xl sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[400px] sm:rounded-2xl"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.2 }}
    >
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span
            className="grid size-8 place-items-center rounded-full bg-volt font-display text-sm font-bold text-volt-foreground"
            aria-hidden="true"
          >
            K
          </span>
          <div className="grid">
            <h2 id="kara-title" className="text-base leading-tight font-semibold">
              Kara
            </h2>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  online ? "bg-success" : state === "offline" ? "bg-warning" : "bg-muted-foreground",
                )}
                aria-hidden="true"
              />
              {t(`kara.${state}`)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {turns.length ? (
            <Button variant="ghost" size="icon-sm" onClick={chat.restart} aria-label={t("kara.restart")}>
              <IconRestart className="size-4.5" aria-hidden="true" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label={t("shell.close")}
            data-kara-close=""
          >
            <IconClose className="size-5" aria-hidden="true" />
          </Button>
        </div>
      </header>

      <div
        ref={list}
        className="grid flex-1 content-start gap-3 overflow-y-auto px-4 py-4"
        aria-live="polite"
      >
        {turns.length === 0 ? (
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">{t("kara.intro")}</p>
            {state === "unset" ? (
              <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground">
                {t("kara.unsetHint")}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  disabled={!online}
                  className="rounded-full border border-border px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {turns.map((m, i) => (
          <div
            key={i}
            className={cn(
              "grid max-w-[88%] gap-1",
              m.role === "user" ? "justify-self-end" : "justify-self-start",
            )}
          >
            {m.role === "user" ? (
              <p className="rounded-2xl rounded-br-md bg-foreground px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-background [overflow-wrap:anywhere]">
                {m.content}
              </p>
            ) : (
              <KaraText
                text={m.content}
                className="min-w-0 rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm leading-relaxed [overflow-wrap:anywhere]"
              />
            )}
            {m.tools?.length ? (
              <p className="px-1 font-mono text-[0.68rem] text-muted-foreground">
                {t("kara.consulted", { tools: m.tools.join(", ") })}
              </p>
            ) : null}
          </div>
        ))}
        {pending ? (
          <p className="flex items-center gap-2 justify-self-start rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm text-muted-foreground">
            <span
              className="size-3 animate-spin rounded-full border-2 border-current border-r-transparent"
              aria-hidden="true"
            />
            {t("kara.thinking")}
          </p>
        ) : null}
        {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
      </div>

      <form onSubmit={submit} className="flex items-end gap-2 border-t border-border p-3">
        <label htmlFor="kara-input" className="sr-only">
          {t("kara.ask")}
        </label>
        <textarea
          id="kara-input"
          ref={input}
          rows={1}
          value={draft}
          maxLength={MAX_CHARS}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send(draft);
            }
          }}
          placeholder={
            online ? t("kara.ask") : state === "offline" ? t("kara.offlineHint") : t("kara.unsetHint")
          }
          disabled={!online}
          className="field-sizing-content max-h-32 min-h-10 flex-1 resize-none rounded-lg border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/20"
        />
        <Button
          type="submit"
          variant="volt"
          size="icon"
          disabled={!draft.trim() || pending || !online}
          aria-label={t("kara.send")}
        >
          <IconSend className="size-5" aria-hidden="true" />
        </Button>
      </form>
    </motion.aside>
  );
}
